import { useRouter } from "next/router";
import { MouseEvent, useMemo } from 'react';
import { ArrowDownIcon, ArrowRightIcon } from '@kursk/components/ui/icons';
import { Button } from "@kursk/components/ui/Button/Button";
import style from './EntitiesGrid.module.scss';
import classNames from 'classnames';
import { loadEntities, setEntitiesOffset } from '@common/redux/slices/entities.slice';
import { useDispatch } from 'react-redux';
import { useDispatchThunk } from '@common/redux/dispatchThunk';
import { EntityType, EntityTypeOnRussian } from "@common/redux/types/common";
import useScrollRestoration from "@common/hooks/useScrollRestoration";
import { themeValue } from "@kursk/themes";
import { BelDownArrowIcon } from "@kursk/components/ui/icons.bel";

const LIMIT = 20;
const OFFSET = 20;

interface IEntitiesGrid<T> {
  items: T[];
  total: number;
  renderGridCard: (arg0: T, index: number) => JSX.Element;
  offset: number;
  entityType: EntityType;
  customStyle?: {
    readonly [key: string]: string;
  },
  columns?: 2 | 4;
  pushkinPage?: boolean;
}

export function EntitiesGrid<T>({
  items,
  total,
  renderGridCard,
  entityType,
  customStyle,
  pushkinPage = false,
  columns = 4
}: IEntitiesGrid<T>) {
  const router = useRouter();
  const styles = customStyle || style;
  const dispatch = useDispatch();
  const dispatchThunk = useDispatchThunk();

  const itemsCount = items.length;

  const hasNext = () => total - itemsCount > 0;
  const getNextCount = () => total - itemsCount > LIMIT ? LIMIT : total - itemsCount;

  const next = async (event: MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();

    const nextOffset = +router.query.limit || OFFSET;
    dispatch(setEntitiesOffset(entityType, nextOffset));
    await router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        limit: nextOffset + OFFSET,
      }

    }, undefined, { shallow: true })
    await dispatchThunk(loadEntities(entityType, {}) as any);
  }

  const getNotFoundMessage = () => {
    if (entityType === 'ticketsbundles') {
      return `К сожалению, абонементы не найдены.`;
    } else if (entityType === 'streams' || entityType === 'regionvideos'
      || entityType === 'museums' || entityType === 'artefacts' || entityType === 'halls') {
      return `К сожалению, ${EntityTypeOnRussian[entityType]} не найдены.`;
    } else return `К сожалению, по вашему запросу ${EntityTypeOnRussian[entityType]} не найдены.`;
  }
  useScrollRestoration(router, items)

  const getGridClass = useMemo(() => {
    if (pushkinPage || columns === 2) {
      return
    } else if (items.length > 17) {
      return styles.cardsGrid_20
    } else if (items.length > 14) {
      return styles.cardsGrid_17
    } else if (items.length > 11) {
      return styles.cardsGrid_14
    } else if (items.length > 9) {
      return styles.cardsGrid_11
    } else if (items.length > 6) {
      return styles.cardsGrid_9
    } else if (items.length > 3) {
      return styles.cardsGrid_6
    } else if (items.length >= 0) {
      return styles.cardsGrid_3
    }
  }, [items])

  const renderNotFound = () => <div className={styles.notFound}>{getNotFoundMessage()}</div>;
  return (
    total ? (
      <>
        {pushkinPage && <div className={styles.eventsTableHeader}>
          <div className={styles.eventsTableTitle}>АФИША ПУШКИНСКОЙ КАРТЫ</div>
          <div className={styles.buttonContainer}>
            <Button
              className={styles.pushkinButton}
              href={'/events/tag-pushkinskaya-karta'}
            >
              <div className={style.pushkinButtonInner}>
                <div>Показать все</div>
                {themeValue({
                  bel: <BelDownArrowIcon/>,
                  default: <ArrowRightIcon/>
                })}
              </div>
            </Button>
          </div>
        </div>}
        <div
          className={classNames([
            columns === 4 && pushkinPage ? styles.pushkinGrid : styles.cardsGrid,
            columns === 2 && styles.cardsGridTwo,
            getGridClass,
          ])}>
          {items.map(renderGridCard)}
        </div>
        {!pushkinPage && hasNext() && (
          <div className={styles.buttonContainer}>
            <button className={classNames(styles.button, styles.showMoreButton)} onClick={next}>
              Показать еще {getNextCount()}
              <ArrowDownIcon width={12} height={5}/>
            </button>
          </div>
        )}
      </>
    ) : renderNotFound()
  )
}
